(() => {
  const state = {
    lastAddedHandles: null,
    renderSeq: 0,
    isRendering: false,
  };

  function getPdpHandles() {
    try {
      const el = document.getElementById('pdp-included-training');
      if (!el) return null;
      const arr = JSON.parse(el.textContent || '[]');
      return Array.isArray(arr) && arr.length ? arr : null;
    } catch (e) {
      return null;
    }
  }

  function getHandlesToRender() {
    const pdp = getPdpHandles();
    if (pdp && pdp.length) return pdp;
    return state.lastAddedHandles && state.lastAddedHandles.length ? state.lastAddedHandles : null;
  }

  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html || '';
    return tmp.textContent || tmp.innerText || '';
  }

  function truncate(str, n) {
    if (!str) return '';
    if (str.length <= n) return str;
    return str.slice(0, n - 1) + '…';
  }

  async function fetchCart() {
    const res = await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() });
    return res.json();
  }

  async function fetchProduct(handle) {
    try {
      // First, fetch from .js endpoint to get product metadata and first 250 variants
      console.log(`[Included Training] Fetching ${handle} from .js endpoint...`);
      const jsRes = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      const productData = await jsRes.json();
      console.log(`[Included Training] .js endpoint returned ${productData.variants ? productData.variants.length : 0} variants`);
      
      // Then, fetch HTML to extract complete variant data
      try {
        console.log(`[Included Training] Fetching HTML for ${handle}...`);
        const htmlRes = await fetch(`/products/${handle}`, { credentials: 'same-origin' });
        const html = await htmlRes.text();
        console.log(`[Included Training] HTML fetched, length: ${html.length}`);
        
        // Parse HTML into DOM for querying elements
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        console.log(`[Included Training] HTML parsed into DOM`);
        
        // Extract all locations from HTML form
        const locationFieldset = Array.from(doc.querySelectorAll('fieldset')).find(fieldset => {
          const legend = fieldset.querySelector('legend');
          if (!legend) return false;
          const legendText = legend.textContent || '';
          return legendText.toLowerCase().includes('location');
        });
        
        const locations = new Set();
        const locationAvailability = new Map(); // Track availability from HTML form
        const locationInputs = locationFieldset 
          ? locationFieldset.querySelectorAll('input[type="radio"][value]')
          : doc.querySelectorAll('input[name*="Location"][type="radio"][value]');
        
        locationInputs.forEach(input => {
          const loc = input.value?.trim();
          if (loc) {
            locations.add(loc);
            // Check if location is disabled in HTML form (disabled class means no availability)
            const isDisabled = input.classList.contains('disabled') || input.disabled;
            locationAvailability.set(loc, !isDisabled);
          }
        });
        
        console.log(`[Included Training] Found ${locations.size} locations in HTML form:`, Array.from(locations));
        console.log(`[Included Training] Location availability from HTML:`, Object.fromEntries(locationAvailability));
        
        // Extract all dates from HTML form
        const dateFieldset = Array.from(doc.querySelectorAll('fieldset')).find(fieldset => {
          const legend = fieldset.querySelector('legend');
          if (!legend) return false;
          const legendText = legend.textContent || '';
          return legendText.toLowerCase().includes('date');
        });
        
        const dates = new Set();
        const dateInputs = dateFieldset
          ? dateFieldset.querySelectorAll('input[type="radio"][value]')
          : doc.querySelectorAll('input[name*="Date"][type="radio"][value]');
        
        dateInputs.forEach(input => {
          const date = input.value?.trim();
          if (date && date !== 'undefined') dates.add(date);
        });
        
        console.log(`[Included Training] Found ${dates.size} dates in HTML form:`, Array.from(dates));
        
        // Parse var meta object from HTML
        let metaVariants = [];
        const startIdx = html.indexOf('var meta = ');
        if (startIdx !== -1) {
          try {
            console.log(`[Included Training] Found 'var meta = ' at position ${startIdx}`);
            const jsonStart = startIdx + 'var meta = '.length;
            const jsonStr = html.substring(jsonStart);
            let braceCount = 0;
            let endIdx = -1;
            let inString = false;
            let escapeNext = false;
            
            // Improved brace counting that handles strings properly
            for (let i = 0; i < jsonStr.length; i++) {
              const char = jsonStr[i];
              
              if (escapeNext) {
                escapeNext = false;
                continue;
              }
              
              if (char === '\\') {
                escapeNext = true;
                continue;
              }
              
              if (char === '"' && !escapeNext) {
                inString = !inString;
                continue;
              }
              
              if (!inString) {
                if (char === '{') braceCount++;
                else if (char === '}') {
                  braceCount--;
                  if (braceCount === 0) {
                    endIdx = i + 1;
                    break;
                  }
                }
              }
            }
            
            if (endIdx !== -1) {
              const jsonText = jsonStr.substring(0, endIdx);
              console.log(`[Included Training] Extracted JSON length: ${jsonText.length} chars`);
              const meta = JSON.parse(jsonText);
              metaVariants = meta?.product?.variants || [];
              console.log(`[Included Training] Parsed ${metaVariants.length} variants from meta object`);
            }
          } catch (parseError) {
            console.error(`[Included Training] Error parsing var meta:`, parseError);
          }
        }
        
        // Build variant map from API variants (first 250)
        const apiVariantMap = new Map();
        productData.variants.forEach(v => {
          const key = `${v.option1 || v.options[0]}|${v.option2 || (v.options && v.options[1]) || ''}`;
          apiVariantMap.set(key, v);
        });
        
        // Build variant map from meta variants
        const metaVariantMap = new Map();
        metaVariants.forEach(v => {
          // Parse public_title format: "Location / Date"
          const parts = v.public_title?.split(' / ') || [];
          if (parts.length >= 2) {
            const key = `${parts[0]}|${parts[1]}`;
            // Check if this variant exists in API (use API's availability if available)
            const apiVariant = apiVariantMap.get(key);
            metaVariantMap.set(key, {
              id: v.id,
              price: v.price || productData.variants[0]?.price || 0,
              available: apiVariant ? apiVariant.available : true, // Use API availability if available, otherwise assume available if in meta
              options: [parts[0], parts[1]],
              option1: parts[0],
              option2: parts[1],
              option3: null,
              title: v.name || v.public_title,
              _fromMeta: !apiVariant // Only mark as from meta if not in API
            });
          }
        });
        
        console.log(`[Included Training] API variant map: ${apiVariantMap.size} variants`);
        console.log(`[Included Training] Meta variant map: ${metaVariantMap.size} variants`);
        
        // Build complete variant list
        const allVariants = [];
        const variantKeys = new Set();
        
        // Add all API variants first, ensuring they have consistent structure
        productData.variants.forEach(v => {
          const key = `${v.option1 || v.options[0]}|${v.option2 || (v.options && v.options[1]) || ''}`;
          if (!variantKeys.has(key)) {
            variantKeys.add(key);
            // Normalize variant structure: ensure it has both options array and option1/option2
            const normalizedVariant = { ...v };
            // Mark as from API - these have the most reliable availability data
            normalizedVariant._fromAPI = true;
            if (!normalizedVariant.options || normalizedVariant.options.length === 0) {
              // Construct options array from option1/option2/option3
              normalizedVariant.options = [];
              if (normalizedVariant.option1) normalizedVariant.options.push(normalizedVariant.option1);
              if (normalizedVariant.option2) normalizedVariant.options.push(normalizedVariant.option2);
              if (normalizedVariant.option3) normalizedVariant.options.push(normalizedVariant.option3);
            }
            // Ensure option1/option2/option3 are set from options array if missing
            if (!normalizedVariant.option1 && normalizedVariant.options && normalizedVariant.options.length > 0) {
              normalizedVariant.option1 = normalizedVariant.options[0];
            }
            if (!normalizedVariant.option2 && normalizedVariant.options && normalizedVariant.options.length > 1) {
              normalizedVariant.option2 = normalizedVariant.options[1];
            }
            if (!normalizedVariant.option3 && normalizedVariant.options && normalizedVariant.options.length > 2) {
              normalizedVariant.option3 = normalizedVariant.options[2];
            }
            allVariants.push(normalizedVariant);
          }
        });
        
        // Add meta variants that aren't in API
        metaVariantMap.forEach((v, key) => {
          if (!variantKeys.has(key)) {
            variantKeys.add(key);
            allVariants.push(v);
          }
        });
        
        // Determine locations with availability from both variant data and HTML form
        const locationsWithAvailability = new Set();
        allVariants.forEach(v => {
          const loc = v.option1 || v.options[0];
          if (loc && v.available) {
            locationsWithAvailability.add(loc);
          }
        });
        
        // Also add locations that are enabled in HTML form (even if not in API/meta yet)
        locationAvailability.forEach((isAvailable, loc) => {
          if (isAvailable) {
            locationsWithAvailability.add(loc);
          }
        });
        
        // Generate variants for all location+date combinations that don't exist yet
        locations.forEach(loc => {
          // Only process locations that have availability (from variants or HTML form)
          if (!locationsWithAvailability.has(loc)) {
            console.log(`[Included Training] Skipping location ${loc} - no availability`);
            return;
          }
          
          dates.forEach(date => {
            const key = `${loc}|${date}`;
            if (!variantKeys.has(key)) {
              // Try to find variant in meta by public_title pattern
              const metaVariant = metaVariants.find(v => {
                const parts = v.public_title?.split(' / ') || [];
                return parts[0] === loc && parts[1] === date;
              });
          
              if (metaVariant) {
                // Found in meta, use real variant data
                // Check if this variant also exists in API for accurate availability
                const apiVariant = apiVariantMap.get(key);
                allVariants.push({
                  id: metaVariant.id,
                  price: metaVariant.price || productData.variants[0]?.price || 0,
                  available: apiVariant ? apiVariant.available : true, // Use API availability if available
                  options: [loc, date],
                  option1: loc,
                  option2: date,
                  option3: null,
                  title: metaVariant.name || metaVariant.public_title,
                  _fromMeta: !apiVariant
                });
                variantKeys.add(key);
              } else {
                // Variant not found in meta - this means it doesn't exist or is beyond the 250 limit
                // Since location has availability, we know some variants exist for this location
                // But we can't create a variant without a real ID, so we'll skip it
                // The date will still appear in dropdown but be disabled if no variant exists
                console.log(`[Included Training] Location ${loc} has availability but variant ${key} not found in meta`);
              }
            }
          });
        });
        
        // Update product data with complete variant list
        productData.variants = allVariants;
        
        // Store location availability from HTML for use in buildOptionSelects
        productData._locationAvailability = locationAvailability;
        
        // Ensure options array is properly structured with all values
        // Shopify API returns options as array of option names, but we need values too
        if (productData.options && productData.options.length >= 2) {
          // Convert options to objects with name and values if they're not already
          const optionsArray = productData.options.map((opt, idx) => {
            if (typeof opt === 'object' && opt !== null) {
              // Already an object, ensure it has values
              if (!opt.values) {
                opt.values = idx === 0 ? Array.from(locations) : Array.from(dates);
              }
              return opt;
            } else {
              // String option name, convert to object
              return {
                name: opt,
                values: idx === 0 ? Array.from(locations) : Array.from(dates)
              };
            }
          });
          productData.options = optionsArray;
        }
        
        console.log(`[Included Training] FINAL: Built ${allVariants.length} variants total`);
        console.log(`[Included Training] FINAL: ${allVariants.filter(v => v.available).length} available variants`);
        
      } catch (htmlError) {
        console.error(`[Included Training] Error fetching/parsing HTML for ${handle}:`, htmlError);
        // Continue with .js data only
      }
      
      // Final debug output before returning
      console.log(`[Included Training] FINAL: Returning product with ${productData.variants.length} variants`);
      
      return productData;
      
    } catch (error) {
      console.error(`[Included Training] Error fetching product ${handle}:`, error);
      // Final fallback
      const res = await fetch(`/products/${handle}.js`, { credentials: 'same-origin' });
      return res.json();
    }
  }

  function getVariantByOptions(product, selectedOptions) {
    console.log(`[Included Training DEBUG] getVariantByOptions called:`);
    console.log(`  - Searching for options:`, selectedOptions);
    console.log(`  - Total variants to search: ${product.variants.length}`);
    
    const matchingVariants = product.variants.filter((v) => {
      // Try to get options array - use options array if available, otherwise construct from option1/option2
      let variantOptions = v.options;
      if (!variantOptions || variantOptions.length === 0) {
        // Fallback: construct options array from option1/option2/option3
        variantOptions = [];
        if (v.option1) variantOptions.push(v.option1);
        if (v.option2) variantOptions.push(v.option2);
        if (v.option3) variantOptions.push(v.option3);
      }
      
      // Check if lengths match
      if (!variantOptions || variantOptions.length !== selectedOptions.length) {
        return false;
      }
      
      // Check each option matches
      return variantOptions.every((opt, idx) => {
        const match = opt === selectedOptions[idx];
        if (!match) {
          console.log(`    - Variant ${v.id} mismatch at index ${idx}: "${opt}" !== "${selectedOptions[idx]}"`);
        }
        return match;
      });
    });
    
    console.log(`  - Matching variants found: ${matchingVariants.length}`);
    if (matchingVariants.length > 0) {
      const found = matchingVariants[0];
      console.log(`  - Returning variant:`, {
        id: found.id,
        options: found.options,
        option1: found.option1,
        option2: found.option2,
        available: found.available,
        _placeholder: found._placeholder
      });
      return found;
    } else {
      console.log(`  - No matching variant found`);
      // Log sample variants to help debug
      const sampleVariants = product.variants.slice(0, 5).map(v => ({
        id: v.id,
        options: v.options,
        option1: v.option1,
        option2: v.option2
      }));
      console.log(`  - Sample variants in product:`, sampleVariants);
      return undefined;
    }
  }

  function findFirstAvailableVariant(product) {
    return product.variants.find((v) => v.available) || product.variants[0];
  }

  function hasVariantsForLocation(product, location) {
    const matchingVariants = product.variants.filter(v => 
      (v.option1 || (v.options && v.options[0])) === location
    );
    
    console.log(`[Included Training DEBUG] hasVariantsForLocation check:`);
    console.log(`  - Location: "${location}"`);
    console.log(`  - Total variants in product: ${product.variants.length}`);
    console.log(`  - Variants matching location: ${matchingVariants.length}`);
    if (matchingVariants.length > 0) {
      console.log(`  - Sample variants:`, matchingVariants.slice(0, 3).map(v => ({
        id: v.id,
        option1: v.option1,
        option2: v.option2,
        available: v.available,
        _placeholder: v._placeholder,
        _fromMeta: v._fromMeta
      })));
    } else {
      console.log(`  - No variants found for this location`);
    }
    
    return matchingVariants.length > 0;
  }

  function getLocationIndex(product, location) {
    // Get all location values from product options
    const locationOption = product.options[0];
    const locations = typeof locationOption === 'object' && locationOption !== null && Array.isArray(locationOption.values)
      ? locationOption.values
      : [...new Set(product.variants.map((v) => v.option1 || (v.options && v.options[0])).filter(Boolean))];
    
    return locations.indexOf(location);
  }

  function createViewDatesLink(product, location, variantId) {
    const field = document.createElement('div');
    field.className = 'product-form__input product-form__input--dropdown';
    
    const link = document.createElement('a');
    // Always use just the product URL with no parameters
    link.href = `/products/${product.handle}`;
    link.className = 'text-sm font-body-bolder reversed-link';
    link.textContent = 'Click to view all dates';
    
    field.appendChild(link);
    return field;
  }

  async function fetchVariantsForLocation(handle, location) {
    try {
      console.log(`[Included Training DEBUG] ========== fetchVariantsForLocation START ==========`);
      console.log(`[Included Training DEBUG] Fetching variants for location: "${location}"`);
      console.log(`[Included Training DEBUG] Product handle: "${handle}"`);
      
      // Fetch HTML with location pre-selected to get accurate date availability
      // This ensures Shopify's server-side rendering returns HTML with correct disabled states
      // Try multiple URL parameter formats to ensure Shopify recognizes the location
      const locationParam = encodeURIComponent(location);
      let productUrl = `/products/${handle}?options[Location]=${locationParam}`;
      console.log(`[Included Training DEBUG] Fetching product page with location parameter: ${productUrl}`);
      
      // Also try alternative format in case Shopify uses different parameter name
      const altUrl = `/products/${handle}?option1=${locationParam}`;
      console.log(`[Included Training DEBUG] Alternative URL format: ${altUrl}`);
      
      const htmlRes = await fetch(productUrl, { credentials: 'same-origin' });
      const html = await htmlRes.text();
      console.log(`[Included Training DEBUG] HTML fetched, length: ${html.length}`);
      
      // Verify the URL was correct by checking if the HTML contains the location
      if (!html.includes(location)) {
        console.warn(`[Included Training DEBUG] WARNING: Fetched HTML does not contain location "${location}" - URL parameter may not have worked`);
      }
      
      // Parse HTML into DOM
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Try to parse var meta object
      let metaVariants = [];
      const startIdx = html.indexOf('var meta = ');
      console.log(`[Included Training DEBUG] Looking for 'var meta = ' in HTML, found at index: ${startIdx}`);
      
      if (startIdx !== -1) {
        try {
          const jsonStart = startIdx + 'var meta = '.length;
          const jsonStr = html.substring(jsonStart);
          let braceCount = 0;
          let endIdx = -1;
          let inString = false;
          let escapeNext = false;
          
          for (let i = 0; i < jsonStr.length; i++) {
            const char = jsonStr[i];
            
            if (escapeNext) {
              escapeNext = false;
              continue;
            }
            
            if (char === '\\') {
              escapeNext = true;
              continue;
            }
            
            if (char === '"' && !escapeNext) {
              inString = !inString;
              continue;
            }
            
            if (!inString) {
              if (char === '{') braceCount++;
              else if (char === '}') {
                braceCount--;
                if (braceCount === 0) {
                  endIdx = i + 1;
                  break;
                }
              }
            }
          }
          
          if (endIdx !== -1) {
            const jsonText = jsonStr.substring(0, endIdx);
            const meta = JSON.parse(jsonText);
            metaVariants = meta?.product?.variants || [];
            console.log(`[Included Training DEBUG] Parsed meta object successfully`);
            console.log(`[Included Training DEBUG] Total variants in meta: ${metaVariants.length}`);
            
            // Log sample of meta variants to see what locations are included
            if (metaVariants.length > 0) {
              const sampleLocations = [...new Set(metaVariants.slice(0, 50).map(v => {
                const parts = v.public_title?.split(' / ') || [];
                return parts[0];
              }))];
              console.log(`[Included Training DEBUG] Sample locations in meta (first 50 variants):`, sampleLocations);
            }
          } else {
            console.warn(`[Included Training DEBUG] Could not find end of meta object`);
          }
        } catch (parseError) {
          console.error(`[Included Training DEBUG] Error parsing meta for location ${location}:`, parseError);
        }
      } else {
        console.warn(`[Included Training DEBUG] 'var meta = ' not found in HTML`);
      }
      
      // Filter variants for this location from meta
      console.log(`[Included Training DEBUG] Filtering meta variants for location "${location}"...`);
      const beforeFilter = metaVariants.length;
      let locationVariants = metaVariants
        .filter(v => {
          const parts = v.public_title?.split(' / ') || [];
          const matches = parts[0] === location;
          if (matches) {
            console.log(`[Included Training DEBUG]   - Found matching variant: ${v.public_title} (ID: ${v.id})`);
          }
          return matches;
        })
        .map(v => {
          const parts = v.public_title?.split(' / ') || [];
          return {
            id: v.id, // Meta variants always have IDs
            price: v.price || 0,
            available: true, // Meta doesn't have availability, assume available if in meta
            options: [parts[0], parts[1]],
            option1: parts[0],
            option2: parts[1],
            option3: null,
            title: v.name || v.public_title,
            _fromMeta: true,
            _fetchedForLocation: location
          };
        });
      
      console.log(`[Included Training DEBUG] Filtered ${beforeFilter} meta variants, found ${locationVariants.length} for location "${location}"`);
      
      // If no variants found in meta (location is beyond 250 limit), try to get variant ID from the product page
      // by looking at the currently selected variant on the page
      if (locationVariants.length === 0) {
        console.log(`[Included Training DEBUG] No variants in meta for "${location}", trying to extract from page state...`);
        
        // Look for the selected variant ID in the page's JavaScript/data
        // Check for data-selected-variant or similar
        const selectedVariantScript = doc.querySelector('script[data-selected-variant]');
        if (selectedVariantScript) {
          try {
            const variantData = JSON.parse(selectedVariantScript.textContent);
            if (variantData && variantData.id) {
              const variantLocation = variantData.option1 || (variantData.options && variantData.options[0]);
              if (variantLocation === location) {
                console.log(`[Included Training DEBUG] Found selected variant ID ${variantData.id} from page state`);
                locationVariants.push({
                  id: variantData.id,
                  price: variantData.price || 0,
                  available: variantData.available !== false,
                  options: variantData.options || [variantData.option1, variantData.option2],
                  option1: variantData.option1,
                  option2: variantData.option2,
                  option3: variantData.option3,
                  title: variantData.title || variantData.name,
                  _fromPageState: true,
                  _fetchedForLocation: location
                });
              }
            }
          } catch (e) {
            console.warn(`[Included Training DEBUG] Could not parse selected variant data:`, e);
          }
        }
        
        // Also check for variant ID in the product form or URL
        const productForm = doc.querySelector('form[action*="/cart/add"]');
        if (productForm) {
          const variantInput = productForm.querySelector('input[name="id"]');
          if (variantInput && variantInput.value) {
            const variantId = parseInt(variantInput.value);
            if (variantId && !isNaN(variantId)) {
              console.log(`[Included Training DEBUG] Found variant ID ${variantId} from product form`);
              // Get the location from the current page state to verify it matches
              const locationInput = locationFieldset?.querySelector('input[type="radio"]:checked, input[type="radio"][checked]');
              if (locationInput && locationInput.value === location) {
                locationVariants.push({
                  id: variantId,
                  price: 0,
                  available: true,
                  options: [location, ''],
                  option1: location,
                  option2: '',
                  option3: null,
                  title: `${location}`,
                  _fromForm: true,
                  _fetchedForLocation: location
                });
              }
            }
          }
        }
      }
      
      // Always try to extract dates from form structure to supplement meta variants
      // Meta may only have partial data (first 250 variants), so we need form structure for all dates
      const metaDates = new Set(locationVariants.map(v => v.option2 || (v.options && v.options[1])));
      console.log(`[Included Training DEBUG] Dates from meta: ${metaDates.size}`);
      console.log(`[Included Training DEBUG] Extracting ALL dates from form structure to supplement meta variants...`);
      
      // Find the location input to verify the selected location in the HTML
      const locationFieldset = Array.from(doc.querySelectorAll('fieldset')).find(fieldset => {
        const legend = fieldset.querySelector('legend');
        if (!legend) return false;
        const legendText = legend.textContent || '';
        return legendText.toLowerCase().includes('location');
      });
      
      console.log(`[Included Training DEBUG] Location fieldset found: ${!!locationFieldset}`);
      
      // Verify which location is actually selected in the fetched HTML
      let actualSelectedLocation = null;
      if (locationFieldset) {
        const selectedLocationInput = Array.from(locationFieldset.querySelectorAll('input[type="radio"]')).find(
          input => input.checked || input.hasAttribute('checked')
        );
        if (selectedLocationInput) {
          actualSelectedLocation = selectedLocationInput.value;
          console.log(`[Included Training DEBUG] HTML shows selected location: "${actualSelectedLocation}" (requested: "${location}")`);
          if (actualSelectedLocation !== location) {
            console.warn(`[Included Training DEBUG] WARNING: HTML location (${actualSelectedLocation}) does not match requested location (${location})!`);
          }
        }
      }
      
      const formVariants = [];
      if (locationFieldset) {
        const locationInput = Array.from(locationFieldset.querySelectorAll('input[type="radio"]')).find(
          input => input.value === location
        );
        
        console.log(`[Included Training DEBUG] Location input found: ${!!locationInput}`);
        if (locationInput) {
          console.log(`[Included Training DEBUG] Location input disabled: ${locationInput.classList.contains('disabled')}`);
          console.log(`[Included Training DEBUG] Location input checked: ${locationInput.checked || locationInput.hasAttribute('checked')}`);
        }
        
        if (locationInput && !locationInput.classList.contains('disabled')) {
          // Location is available, try to get dates from date fieldset
          const dateFieldset = Array.from(doc.querySelectorAll('fieldset')).find(fieldset => {
            const legend = fieldset.querySelector('legend');
            if (!legend) return false;
            const legendText = legend.textContent || '';
            return legendText.toLowerCase().includes('date');
          });
          
          console.log(`[Included Training DEBUG] Date fieldset found: ${!!dateFieldset}`);
          
          if (dateFieldset) {
            // Extract all dates from form - these have real variant IDs we can use
            const dateInputs = dateFieldset.querySelectorAll('input[type="radio"][value]');
            const formDates = Array.from(dateInputs)
              .map(input => ({
                date: input.value?.trim(),
                variantId: input.getAttribute('data-variant-id') || input.id || null,
                disabled: input.classList.contains('disabled'),
                checked: input.checked || input.hasAttribute('checked')
              }))
              .filter(item => item.date && item.date !== 'undefined');
            
            console.log(`[Included Training DEBUG] Found ${formDates.length} dates in form`);
            console.log(`[Included Training DEBUG] Sample form dates (first 5):`, formDates.slice(0, 5).map(d => ({
              date: d.date,
              disabled: d.disabled,
              checked: d.checked
            })));
            
            // Log availability distribution
            const availableDates = formDates.filter(d => !d.disabled).map(d => d.date);
            const disabledDates = formDates.filter(d => d.disabled).map(d => d.date);
            console.log(`[Included Training DEBUG] Form availability for "${location}": ${availableDates.length} available, ${disabledDates.length} disabled`);
            if (availableDates.length > 0) {
              console.log(`[Included Training DEBUG] Available dates:`, availableDates.slice(0, 10));
            }
            if (disabledDates.length > 0 && disabledDates.length <= 10) {
              console.log(`[Included Training DEBUG] Disabled dates:`, disabledDates);
            }
            
            // For each form date, check if we already have it from meta
            formDates.forEach(formDate => {
              // Check if we already have this variant from meta
              const existingVariant = locationVariants.find(v => 
                (v.option2 || (v.options && v.options[1])) === formDate.date
              );
              
              if (existingVariant) {
                // We have this variant from meta, use its availability (meta variants are marked as available: true)
                // But we should update it with the form's disabled state if it's more accurate
                // Since meta doesn't have availability info, we trust the form's disabled state
                existingVariant.available = !formDate.disabled;
                console.log(`[Included Training DEBUG]   - Updated meta variant: ${formDate.date} (available: ${!formDate.disabled})`);
              } else if (!metaDates.has(formDate.date)) {
                // This date is not in meta, create a variant from form data
                // Try to get variant ID from form input, or use placeholder
                const formVariant = {
                  id: formDate.variantId ? parseInt(formDate.variantId) : null,
                  price: 0,
                  available: !formDate.disabled, // Use form disabled state
                  options: [location, formDate.date],
                  option1: location,
                  option2: formDate.date,
                  option3: null,
                  title: `${location} / ${formDate.date}`,
                  _fromForm: true,
                  _fetchedForLocation: location,
                  _placeholder: !formDate.variantId // Mark as placeholder if no ID
                };
                formVariants.push(formVariant);
                console.log(`[Included Training DEBUG]   - Added form variant: ${formDate.date} (ID: ${formDate.variantId || 'null'}, available: ${!formDate.disabled})`);
              } else {
                console.log(`[Included Training DEBUG]   - Date "${formDate.date}" already in meta variants, skipping`);
              }
            });
            
            console.log(`[Included Training DEBUG] Created ${formVariants.length} additional variants from form for "${location}"`);
            
            // Log summary of availability
            const availableCount = locationVariants.filter(v => v.available).length + formVariants.filter(v => v.available).length;
            const totalCount = locationVariants.length + formVariants.length;
            console.log(`[Included Training DEBUG] Availability summary: ${availableCount}/${totalCount} dates available for "${location}"`);
          }
        }
      }
      
      // Merge form variants with meta variants
      locationVariants = [...locationVariants, ...formVariants];
      console.log(`[Included Training DEBUG] Total variants after merging meta + form: ${locationVariants.length}`);
      
      // If we don't have any variants with IDs, try to extract one from the HTML form
      // Look for the first available date input for this location to get a variant ID
      if (locationVariants.length > 0 && !locationVariants.some(v => v.id && v.id !== null)) {
        console.log(`[Included Training DEBUG] No variants with IDs found, trying to extract from form...`);
        const dateFieldset = Array.from(doc.querySelectorAll('fieldset')).find(fieldset => {
          const legend = fieldset.querySelector('legend');
          if (!legend) return false;
          const legendText = legend.textContent || '';
          return legendText.toLowerCase().includes('date');
        });
        
        if (dateFieldset) {
          const dateInputs = dateFieldset.querySelectorAll('input[type="radio"][value]');
          // Find first non-disabled date input (available date)
          const availableDateInput = Array.from(dateInputs).find(input => 
            !input.classList.contains('disabled') && input.value && input.value !== 'undefined'
          );
          
          if (availableDateInput) {
            // Try to get variant ID from various possible attributes
            let variantId = availableDateInput.getAttribute('data-variant-id') || 
                           availableDateInput.getAttribute('data-variantid') ||
                           availableDateInput.id?.match(/\d+/)?.[0] ||
                           null;
            
            if (variantId) {
              variantId = parseInt(variantId);
              console.log(`[Included Training DEBUG] Extracted variant ID ${variantId} from form input`);
              // Update the first variant with this ID
              if (locationVariants[0]) {
                locationVariants[0].id = variantId;
                locationVariants[0]._placeholder = false;
                console.log(`[Included Training DEBUG] Updated first variant with ID ${variantId}`);
              }
            } else {
              console.warn(`[Included Training DEBUG] Could not extract variant ID from form input`);
            }
          }
        }
      }
      
      console.log(`[Included Training DEBUG] ========== fetchVariantsForLocation END ==========`);
      console.log(`[Included Training DEBUG] Returning ${locationVariants.length} variants for location "${location}"`);
      if (locationVariants.length > 0) {
        console.log(`[Included Training DEBUG] Sample returned variants:`, locationVariants.slice(0, 3).map(v => ({
          id: v.id,
          option1: v.option1,
          option2: v.option2,
          _placeholder: v._placeholder
        })));
      }
      return locationVariants;
    } catch (error) {
      console.error(`[Included Training DEBUG] ========== ERROR in fetchVariantsForLocation ==========`);
      console.error(`[Included Training DEBUG] Location: "${location}"`);
      console.error(`[Included Training DEBUG] Handle: "${handle}"`);
      console.error(`[Included Training DEBUG] Error:`, error);
      console.error(`[Included Training DEBUG] Error stack:`, error.stack);
      return [];
    }
  }

  function renderPriceCents(cents) {
    try {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    } catch (e) {
      return `£${(cents / 100).toFixed(2)}`;
    }
  }

  function buildOptionSelects(product, variant, onChange) {
    console.log(`[Included Training DEBUG] ========== buildOptionSelects START ==========`);
    console.log(`[Included Training DEBUG] Product handle: "${product.handle}"`);
    console.log(`[Included Training DEBUG] Product has ${product.variants.length} variants`);
    console.log(`[Included Training DEBUG] Product options:`, product.options);
    console.log(`[Included Training DEBUG] Initial variant:`, variant ? {
      id: variant.id,
      options: variant.options,
      option1: variant.option1,
      option2: variant.option2,
      available: variant.available
    } : 'null');
    
    // Log variant distribution by location
    const locationCounts = {};
    product.variants.forEach(v => {
      const loc = v.option1 || (v.options && v.options[0]);
      if (loc) {
        locationCounts[loc] = (locationCounts[loc] || 0) + 1;
      }
    });
    console.log(`[Included Training DEBUG] Variant distribution by location:`, locationCounts);
    
    const wrapper = document.createElement('div');
    wrapper.className = 'itd__options grid gap-2';
    product.options.forEach((optName, idx) => {
      const field = document.createElement('div');
      field.className = 'product-form__input product-form__input--dropdown';

      const select = document.createElement('select');
      select.className = 'form-control form-control--select';
      select.setAttribute('data-option-index', String(idx));
      
      // Get values from options array if available (contains all possible values),
      // otherwise fall back to extracting from variants
      let values = [];
      const opt = product.options[idx];
      if (typeof opt === 'object' && opt !== null && Array.isArray(opt.values)) {
        values = opt.values;
      } else if (typeof opt === 'string') {
        // Option is just a name string, extract unique values from variants
        values = [...new Set(product.variants.map((v) => v.options && v.options[idx]).filter(Boolean))];
      } else {
        // Fallback: extract unique values from variants
        values = [...new Set(product.variants.map((v) => v.options && v.options[idx]).filter(Boolean))];
      }
      
      console.log(`[Included Training buildOptionSelects] Option ${idx} (${optName}): found ${values.length} values:`, values);
      
      values.forEach((val) => {
        if (!val) return; // Skip null/undefined values
        
        const optionEl = document.createElement('option');
        optionEl.value = val;
        optionEl.textContent = val;
        optionEl.selected = variant.options && variant.options[idx] === val;
        
        // For Location (first option), disable if no available variants with this location
        // For Date (subsequent options), disable if no variant exists with current location + this date
        if (idx === 0) {
          // Location: check both variant availability and HTML form availability
          const variantsWithThisLocation = product.variants.filter((v) => 
            (v.option1 || (v.options && v.options[0])) === val
          );
          const hasAvailableVariant = variantsWithThisLocation.some(v => v.available);
          
          // Also check HTML form availability if available
          const htmlAvailability = product._locationAvailability?.get(val);
          const isAvailable = hasAvailableVariant || htmlAvailability === true;
          
          optionEl.disabled = !isAvailable;
        } else {
          // Date: filter based on currently selected location (from variant parameter)
          const currentLocation = variant.option1 || (variant.options && variant.options[0]);
          if (idx === 1 && currentLocation) {
            // Initial date option creation - log for debugging
            console.log(`[Included Training DEBUG] Initial date option creation for date "${val}" with location "${currentLocation}"`);
          }
          
          if (currentLocation) {
            // Check if variant exists with current location + this date
            const testOptions = [currentLocation, val];
            const tv = getVariantByOptions(product, testOptions);
            const isDisabled = !tv || !tv.available;
            optionEl.disabled = isDisabled;
            
            if (idx === 1) {
              // Only log for date options (index 1) to avoid spam
              if (isDisabled) {
                console.log(`[Included Training DEBUG] Initial: Date "${val}" DISABLED (variant: ${tv ? 'found but unavailable' : 'not found'})`);
              } else {
                console.log(`[Included Training DEBUG] Initial: Date "${val}" ENABLED (variant ID: ${tv.id})`);
              }
            }
          } else {
            // Fallback: if no location selected, check if any variant exists with this date
            const variantsWithThisDate = product.variants.filter((v) => 
              (v.option2 || (v.options && v.options[1])) === val
            );
            optionEl.disabled = variantsWithThisDate.length === 0;
            if (idx === 1) {
              console.log(`[Included Training DEBUG] Initial: Date "${val}" - no location selected, ${variantsWithThisDate.length} variants with this date`);
            }
          }
        }
        
        select.appendChild(optionEl);
      });

      select.addEventListener('change', async () => {
        const current = product.options.map((_, i) => {
          const sel = wrapper.querySelector(`select[data-option-index="${i}"]`);
          return sel ? sel.value : (variant.options && variant.options[i]);
        });
        
        // If location changed (first option), check if we need to fetch variant data
        const optionIndex = parseInt(select.getAttribute('data-option-index'));
        console.log(`[Included Training DEBUG] ========== Location Change Handler ==========`);
        console.log(`[Included Training DEBUG] Option index: ${optionIndex}, Current values:`, current);
        
        if (optionIndex === 0 && current[0]) {
          const selectedLocation = current[0];
          console.log(`[Included Training DEBUG] Location changed to: "${selectedLocation}"`);
          console.log(`[Included Training DEBUG] Product handle: "${product.handle}"`);
          console.log(`[Included Training DEBUG] Current variant count: ${product.variants.length}`);
          
          // Check if location is 9th or later (index >= 8)
          const locationIndex = getLocationIndex(product, selectedLocation);
          console.log(`[Included Training DEBUG] Location index: ${locationIndex} (9th+ if >= 8)`);
          
          if (locationIndex >= 8) {
            // Location is 9th or later - replace date dropdown with link
            console.log(`[Included Training DEBUG] Location "${selectedLocation}" is 9th+ (index ${locationIndex}), replacing date dropdown with link`);
            
            // Remove existing date dropdown if it exists
            const dateSelect = wrapper.querySelector(`select[data-option-index="1"]`);
            if (dateSelect) {
              const dateField = dateSelect.closest('.product-form__input');
              if (dateField) {
                dateField.remove();
              }
            }
            
            // Remove existing link if it exists (in case user switches between 9th+ locations)
            // Find all fields that contain a link (not a select) - these are our "Click to view all dates" links
            const existingLinkFields = Array.from(wrapper.querySelectorAll('.product-form__input')).filter(field => {
              const hasLink = field.querySelector('a') !== null;
              const hasSelect = field.querySelector('select') !== null;
              return hasLink && !hasSelect; // Link field without a select
            });
            existingLinkFields.forEach(linkField => {
              linkField.remove();
            });
            console.log(`[Included Training DEBUG] Removed ${existingLinkFields.length} existing link field(s)`);
            
            // Fetch one variant for this location to get the variant ID for the URL
            const locationVariants = await fetchVariantsForLocation(product.handle, selectedLocation);
            let variantId = null;
            if (locationVariants.length > 0 && locationVariants[0].id) {
              variantId = locationVariants[0].id;
              console.log(`[Included Training DEBUG] Found variant ID ${variantId} for location "${selectedLocation}"`);
            } else {
              // Try to find variant in existing product variants
              const locationVariant = product.variants.find(v => 
                (v.option1 || (v.options && v.options[0])) === selectedLocation && v.id
              );
              if (locationVariant) {
                variantId = locationVariant.id;
                console.log(`[Included Training DEBUG] Found variant ID ${variantId} in existing variants`);
              }
            }
            
            // Create and add the link with variant ID
            const linkField = createViewDatesLink(product, selectedLocation, variantId);
            wrapper.appendChild(linkField);
            
            // Update variant selection (no date selected when showing link)
            const locationOnlyVariant = product.variants.find(v => 
              (v.option1 || (v.options && v.options[0])) === selectedLocation
            ) || variant;
            onChange(locationOnlyVariant);
            return; // Exit early, no need to continue with variant fetching
          } else {
            // Location is 1st-8th - ensure date dropdown is shown
            console.log(`[Included Training DEBUG] Location "${selectedLocation}" is 1st-8th (index ${locationIndex}), ensuring date dropdown is shown`);
            
            // Remove existing link if it exists
            // Find all fields that contain a link (not a select) - these are our "Click to view all dates" links
            const existingLinkFields = Array.from(wrapper.querySelectorAll('.product-form__input')).filter(field => {
              const hasLink = field.querySelector('a') !== null;
              const hasSelect = field.querySelector('select') !== null;
              return hasLink && !hasSelect; // Link field without a select
            });
            existingLinkFields.forEach(linkField => {
              linkField.remove();
            });
            console.log(`[Included Training DEBUG] Removed ${existingLinkFields.length} existing link field(s)`);
            
            // Ensure date dropdown exists (it should from initial render, but check anyway)
            let dateSelect = wrapper.querySelector(`select[data-option-index="1"]`);
            if (!dateSelect) {
              // Date dropdown doesn't exist, create it
              const dateField = document.createElement('div');
              dateField.className = 'product-form__input product-form__input--dropdown';
              dateSelect = document.createElement('select');
              dateSelect.className = 'form-control form-control--select';
              dateSelect.setAttribute('data-option-index', '1');
              dateField.appendChild(dateSelect);
              wrapper.appendChild(dateField);
            }
          }
          
          const hasVariants = hasVariantsForLocation(product, selectedLocation);
          console.log(`[Included Training DEBUG] hasVariantsForLocation result: ${hasVariants}`);
          
          // Always fetch variants for the selected location to ensure we have ALL dates
          // The initial API response only has 250 variants (first ~8-9 locations), so locations
          // beyond that will have partial or no data. Even if we have some variants, we might
          // only have the first few dates, not all of them.
          // Fetching ensures we get complete data from the HTML form structure.
          if (true) { // Always fetch to ensure complete data
            console.log(`[Included Training DEBUG] Fetching variants for location "${selectedLocation}" (always fetch to ensure all dates are available)...`);
            
            // Show loading state on date dropdown
            const dateSelect = wrapper.querySelector(`select[data-option-index="1"]`);
            console.log(`[Included Training DEBUG] Date select found: ${!!dateSelect}`);
            if (dateSelect) {
              dateSelect.disabled = true;
              const loadingOption = document.createElement('option');
              loadingOption.textContent = 'Loading dates...';
              loadingOption.disabled = true;
              loadingOption.selected = true;
              // Clear existing options
              while (dateSelect.options.length > 0) {
                dateSelect.remove(0);
              }
              dateSelect.appendChild(loadingOption);
            }
            
            // Fetch variants for this location
            const locationVariants = await fetchVariantsForLocation(product.handle, selectedLocation);
            console.log(`[Included Training DEBUG] Fetch returned ${locationVariants.length} variants`);
            
            if (locationVariants.length > 0) {
              console.log(`[Included Training DEBUG] Variants before merge: ${product.variants.length}`);
              
              // Merge new variants into product.variants (avoid duplicates)
              const existingKeys = new Set(product.variants.map(v => 
                `${v.option1 || v.options[0]}|${v.option2 || (v.options && v.options[1]) || ''}`
              ));
              console.log(`[Included Training DEBUG] Existing variant keys: ${existingKeys.size}`);
              
              let addedCount = 0;
              let skippedCount = 0;
              let updatedCount = 0;
              locationVariants.forEach(newVariant => {
                const key = `${newVariant.option1}|${newVariant.option2}`;
                if (!existingKeys.has(key)) {
                  // Normalize variant structure before adding
                  const normalizedVariant = { ...newVariant };
                  if (!normalizedVariant.options || normalizedVariant.options.length === 0) {
                    // Construct options array from option1/option2/option3
                    normalizedVariant.options = [];
                    if (normalizedVariant.option1) normalizedVariant.options.push(normalizedVariant.option1);
                    if (normalizedVariant.option2) normalizedVariant.options.push(normalizedVariant.option2);
                    if (normalizedVariant.option3) normalizedVariant.options.push(normalizedVariant.option3);
                  }
                  // Ensure option1/option2/option3 are set from options array if missing
                  if (!normalizedVariant.option1 && normalizedVariant.options && normalizedVariant.options.length > 0) {
                    normalizedVariant.option1 = normalizedVariant.options[0];
                  }
                  if (!normalizedVariant.option2 && normalizedVariant.options && normalizedVariant.options.length > 1) {
                    normalizedVariant.option2 = normalizedVariant.options[1];
                  }
                  if (!normalizedVariant.option3 && normalizedVariant.options && normalizedVariant.options.length > 2) {
                    normalizedVariant.option3 = normalizedVariant.options[2];
                  }
                  
                  product.variants.push(normalizedVariant);
                  existingKeys.add(key);
                  addedCount++;
                  console.log(`[Included Training DEBUG] Added variant: ${key} (ID: ${normalizedVariant.id}, available: ${normalizedVariant.available}, options: [${normalizedVariant.options.join(', ')}])`);
                } else {
                  // Variant already exists - update its availability if we have more accurate data
                  const existingVariant = product.variants.find(v => 
                    `${v.option1 || (v.options && v.options[0])}|${v.option2 || (v.options && v.options[1])}` === key
                  );
                  
                  if (existingVariant) {
                    // If the existing variant is from API (has real ID and availability), keep its availability
                    // Only update if the new variant has more reliable data (e.g., from form with location-specific fetch)
                    if (newVariant._fetchedForLocation === selectedLocation && !existingVariant._fromAPI) {
                      // New variant was fetched specifically for this location, so it's more accurate
                      const oldAvailable = existingVariant.available;
                      existingVariant.available = newVariant.available;
                      if (oldAvailable !== newVariant.available) {
                        updatedCount++;
                        console.log(`[Included Training DEBUG] Updated variant availability: ${key} (${oldAvailable} -> ${newVariant.available})`);
                      }
                    }
                    skippedCount++;
                    console.log(`[Included Training DEBUG] Skipping duplicate variant: ${key} (keeping existing availability: ${existingVariant.available})`);
                  } else {
                    skippedCount++;
                    console.log(`[Included Training DEBUG] Skipping duplicate variant: ${key} (not found in existing variants)`);
                  }
                }
              });
              
              console.log(`[Included Training DEBUG] Variants after merge: ${product.variants.length}`);
              console.log(`[Included Training DEBUG] Added ${addedCount} variants, updated ${updatedCount} variants, skipped ${skippedCount} duplicates`);
              
              // Verify variants are actually in the array and have correct structure
              const verifyVariants = product.variants.filter(v => 
                (v.option1 || (v.options && v.options[0])) === selectedLocation
              );
              console.log(`[Included Training DEBUG] Verification: ${verifyVariants.length} variants now exist for "${selectedLocation}"`);
              if (verifyVariants.length > 0) {
                console.log(`[Included Training DEBUG] Sample verified variants:`, verifyVariants.slice(0, 3).map(v => ({
                  id: v.id,
                  option1: v.option1,
                  option2: v.option2,
                  options: v.options,
                  hasOptionsArray: !!v.options && v.options.length > 0,
                  hasOption1: !!v.option1,
                  hasOption2: !!v.option2
                })));
              }
            } else {
              console.warn(`[Included Training DEBUG] No variants found for location "${selectedLocation}" after fetch`);
            }
            
            // Re-enable date dropdown and rebuild options
            if (dateSelect) {
              dateSelect.disabled = false;
              dateSelect.innerHTML = ''; // Clear loading message
              
              // Rebuild date options
              const dateOpt = product.options[1];
              const dateValues = typeof dateOpt === 'object' && dateOpt !== null && Array.isArray(dateOpt.values)
                ? dateOpt.values
                : [...new Set(product.variants.map((v) => v.options && v.options[1]).filter(Boolean))];
              
              console.log(`[Included Training DEBUG] Rebuilding date dropdown with ${dateValues.length} dates`);
              console.log(`[Included Training DEBUG] Selected location for date filtering: "${selectedLocation}"`);
              
              let enabledCount = 0;
              let disabledCount = 0;
              
              dateValues.forEach(dateVal => {
                if (!dateVal) return;
                const dateOption = document.createElement('option');
                dateOption.value = dateVal;
                dateOption.textContent = dateVal;
                
                // Check if variant exists with current location + this date
                const testOptions = [selectedLocation, dateVal];
                console.log(`[Included Training DEBUG] Checking date "${dateVal}" with location "${selectedLocation}"...`);
                const tv = getVariantByOptions(product, testOptions);
                const isDisabled = !tv || !tv.available;
                dateOption.disabled = isDisabled;
                
                if (isDisabled) {
                  disabledCount++;
                  console.log(`[Included Training DEBUG]   - Date "${dateVal}" DISABLED (variant: ${tv ? 'found but unavailable' : 'not found'})`);
                } else {
                  enabledCount++;
                  console.log(`[Included Training DEBUG]   - Date "${dateVal}" ENABLED (variant ID: ${tv.id})`);
                }
                
                dateSelect.appendChild(dateOption);
              });
              
              console.log(`[Included Training DEBUG] Date dropdown rebuilt: ${enabledCount} enabled, ${disabledCount} disabled`);
              
              // Final verification - check a few dates to confirm they're correct
              const sampleDates = dateValues.slice(0, 5);
              console.log(`[Included Training DEBUG] Verification - checking sample dates:`);
              sampleDates.forEach(dateVal => {
                const testOptions = [selectedLocation, dateVal];
                const tv = getVariantByOptions(product, testOptions);
                console.log(`[Included Training DEBUG]   - "${dateVal}": ${tv ? `FOUND (ID: ${tv.id}, available: ${tv.available})` : 'NOT FOUND'}`);
              });
            }
          } else {
            console.log(`[Included Training DEBUG] Variants already exist for "${selectedLocation}", skipping fetch`);
            
            // Even if variants exist, verify they're being used correctly and have proper structure
            const existingVariants = product.variants.filter(v => 
              (v.option1 || (v.options && v.options[0])) === selectedLocation
            );
            console.log(`[Included Training DEBUG] Existing variants for "${selectedLocation}": ${existingVariants.length}`);
            if (existingVariants.length > 0) {
              console.log(`[Included Training DEBUG] Sample existing variants:`, existingVariants.slice(0, 3).map(v => ({
                id: v.id,
                option1: v.option1,
                option2: v.option2,
                options: v.options,
                hasOptionsArray: !!v.options && v.options.length > 0,
                available: v.available
              })));
              
              // Verify structure - check if variants can be found by getVariantByOptions
              const sampleDate = existingVariants[0]?.option2 || (existingVariants[0]?.options && existingVariants[0].options[1]);
              if (sampleDate) {
                const testOptions = [selectedLocation, sampleDate];
                const testVariant = getVariantByOptions(product, testOptions);
                console.log(`[Included Training DEBUG] Test lookup for "${selectedLocation}" + "${sampleDate}": ${testVariant ? `FOUND (ID: ${testVariant.id})` : 'NOT FOUND - STRUCTURE ISSUE'}`);
              }
            }
          }
        }
        
        console.log(`[Included Training DEBUG] Getting variant for current selection:`, current);
        const newVariant = getVariantByOptions(product, current) || variant;
        console.log(`[Included Training DEBUG] Selected variant:`, newVariant ? {
          id: newVariant.id,
          options: newVariant.options,
          available: newVariant.available
        } : 'NOT FOUND - using fallback');
        onChange(newVariant);
        
        // Recompute disabled states after change
        console.log(`[Included Training DEBUG] ========== Recomputing disabled states ==========`);
        console.log(`[Included Training DEBUG] Current selection:`, current);
        console.log(`[Included Training DEBUG] Product variant count: ${product.variants.length}`);
        
        product.options.forEach((_, i) => {
          const sel = wrapper.querySelector(`select[data-option-index="${i}"]`);
          if (!sel) return;
          
          console.log(`[Included Training DEBUG] Processing option ${i} (${product.options[i]?.name || product.options[i]}), ${sel.options.length} options`);
          
          [...sel.options].forEach((opt) => {
            if (i === 0) {
              // Location: check both variant availability and HTML form availability
              const variantsWithThisLocation = product.variants.filter((v) => 
                (v.option1 || (v.options && v.options[0])) === opt.value
              );
              const hasAvailableVariant = variantsWithThisLocation.some(v => v.available);
              
              // Also check HTML form availability if available
              const htmlAvailability = product._locationAvailability?.get(opt.value);
              const isAvailable = hasAvailableVariant || htmlAvailability === true;
              
              opt.disabled = !isAvailable;
            } else {
              // Date (or other options): disable if no available variant with current combination
              const test = [...current];
              test[i] = opt.value;
              
              console.log(`[Included Training DEBUG]   - Testing date "${opt.value}" with location "${current[0]}"...`);
              const tv = getVariantByOptions(product, test);
              
              const isDisabled = !tv || !tv.available;
              opt.disabled = isDisabled;
              
              if (isDisabled) {
                console.log(`[Included Training DEBUG]     -> DISABLED (variant: ${tv ? 'found but unavailable' : 'not found'})`);
              } else {
                console.log(`[Included Training DEBUG]     -> ENABLED (variant ID: ${tv.id}, available: ${tv.available})`);
              }
            }
          });
        });
        
        console.log(`[Included Training DEBUG] ========== Disabled states recomputed ==========`);
      });

      field.appendChild(select);
      wrapper.appendChild(field);
    });
    return wrapper;
  }

  function shouldShowOptions(product) {
    if (!product || !product.options || product.options.length === 0) return false;
    // Hide options when product only has the single default variant
    if (product.variants && product.variants.length === 1) {
      const only = product.variants[0];
      const optionNameIsTitle = product.options.length === 1 && String(product.options[0]).toLowerCase() === 'title';
      const titleIsDefault = String(only.title).toLowerCase() === 'default title';
      const optionsAllDefault = Array.isArray(only.options) && only.options.every((o) => String(o).toLowerCase() === 'default title');
      if (optionNameIsTitle || titleIsDefault || optionsAllDefault) return false;
    }
    return true;
  }

  function buildCard(product) {
    console.log(`[Included Training buildCard] Building card for product: ${product.title || product.handle}`);
    console.log(`[Included Training buildCard] Product has ${product.variants.length} variants, ${product.variants.filter(v => v.available).length} available`);
    console.log(`[Included Training buildCard] Product options:`, product.options);
    
    const firstAvailable = findFirstAvailableVariant(product);
    let currentVariant = firstAvailable;

    const li = document.createElement('div');
    li.className = 'itd__card flex gap-3';

    const imgLink = document.createElement('a');
    imgLink.href = product.url;
    imgLink.className = 'blocks-radius media-wrapper itd__thumb';
    if (product.images && product.images.length) {
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.width = 100;
      img.height = 100;
      img.alt = product.title;
      img.src = `${product.featured_image}?width=100&height=100&crop=center`;
      imgLink.appendChild(img);
    }

    const details = document.createElement('div');
    details.className = 'itd__details flex-grow grid gap-2';

    const title = document.createElement('a');
    title.href = product.url;
    title.className = 'reversed-link text-sm font-body-bolder';
    title.textContent = product.title;

    const desc = document.createElement('div');
    desc.className = 'text-sm text-subtext';
    desc.textContent = truncate(stripHtml(product.description), 108);

    const price = document.createElement('div');
    price.className = 'itd__price text-sm text-subtext';
    price.innerHTML = `<s>${renderPriceCents(currentVariant.price)}</s>`;

    const onVariantChange = (v) => {
      currentVariant = v;
      price.innerHTML = `<s>${renderPriceCents(currentVariant.price)}</s>`;
      // Disable button if variant is not available, is a placeholder, or has no ID
      const isPlaceholder = currentVariant._placeholder === true;
      const hasNoId = !currentVariant.id || currentVariant.id === null;
      addBtn.disabled = !currentVariant || !currentVariant.available || isPlaceholder || hasNoId;
    };

    const options = shouldShowOptions(product)
      ? buildOptionSelects(product, currentVariant, onVariantChange)
      : null;

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'btn btn--primary btn--small itd__add';
    addBtn.innerHTML = '<span class="btn__text">Add to basket</span>';
    const isPlaceholder = currentVariant?._placeholder === true;
    const hasNoId = !currentVariant?.id || currentVariant.id === null;
    addBtn.disabled = !currentVariant || !currentVariant.available || isPlaceholder || hasNoId;
    addBtn.addEventListener('click', async () => {
      // Don't allow adding placeholder variants
      if (currentVariant._placeholder || !currentVariant.id) {
        console.error(`[Included Training] Cannot add variant - missing ID or placeholder variant`);
        return;
      }
      
      addBtn.classList.add('btn--loading');
      try {
        // Bundle sections like theme's add flow
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = new FormData();
        body.append('id', String(currentVariant.id));
        body.append('quantity', '1');
        body.append('sections', sectionsToBundle);
        body.append('sections_url', window.location.pathname);

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = body;

        const addRes = await fetch(`${FoxTheme.routes.cart_add_url}`, config);

        let addData = null;
        try {
          const ct = addRes.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            addData = await addRes.json();
            if (addData.status) throw new Error(addData.description || 'Add to cart error');
          }
        } catch (e) {
          // Non-JSON response; fall through to refresh path
        }

        if (addData && addData['sections']) {
          const cartJson = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig() })
          ).json();
          cartJson['sections'] = addData['sections'];
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        } else {
          // Fallback: refresh drawer section to reflect new line item
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(() => {
            if (!state.isRendering) {
              try { render(); } catch (_) {}
            }
          }, 300);
        }
        li.remove();
      } catch (e) {
        console.error(e);
      } finally {
        addBtn.classList.remove('btn--loading');
      }
    });

    details.appendChild(title);
    details.appendChild(desc);
    if (options) details.appendChild(options);
    details.appendChild(price);
    details.appendChild(addBtn);

    li.appendChild(imgLink);
    li.appendChild(details);
    return li;
  }

  function getContainer() {
    return document.getElementById('drawer-included-training');
  }

  function getListContainer() {
    const c = getContainer();
    return c ? c.querySelector('.cart-drawer-included-training__list') : null;
  }

  function getWrapperItem() {
    return document.getElementById('CartDrawer-IncludedTraining');
  }

  function checkContainerVisibility() {
    const list = getListContainer();
    const container = getContainer();
    const wrapper = getWrapperItem();
    if (!list || (!container && !wrapper)) return;
    const shouldHide = list.children.length === 0;
    if (container) container.hidden = shouldHide;
    if (container) {
      const heading = container.querySelector('h3');
      if (heading) heading.hidden = shouldHide ? true : false;
    }
    if (wrapper) wrapper.hidden = shouldHide;
  }

  async function render() {
    // Prevent concurrent renders
    if (state.isRendering) return;
    state.isRendering = true;
    
    const seq = ++state.renderSeq;
    const handles = getHandlesToRender();
    const container = getContainer();
    const list = getListContainer();
    if (!container || !list) {
      state.isRendering = false;
      return;
    }

    // Reset UI hidden by default until we confirm there are items
    list.innerHTML = '';
    const wrapper = getWrapperItem();
    if (wrapper) wrapper.hidden = true;
    container.hidden = true;
    const heading = container.querySelector('h3');
    if (heading) heading.hidden = true;
    if (!handles || !handles.length) {
      state.isRendering = false;
      return;
    }

    // Dedupe handles to avoid duplicates if metafield or events duplicate entries
    const uniqueHandles = Array.from(new Set(handles));

    try {
      const cart = await fetchCart();
      const cartHandles = new Set((cart.items || []).map((li) => li.handle));
      const filtered = uniqueHandles.filter((h) => !cartHandles.has(h));
      if (!filtered.length) {
        checkContainerVisibility();
        state.isRendering = false;
        return;
      }

      const products = await Promise.all(
        filtered.map(async (h) => {
          try {
            return await fetchProduct(h);
          } catch (e) {
            return null;
          }
        })
      );
      const valid = products.filter(Boolean);
      // Abort if a newer render started
      if (seq !== state.renderSeq) {
        state.isRendering = false;
        return;
      }
      valid.forEach((p) => list.appendChild(buildCard(p)));
      checkContainerVisibility();
    } catch (e) {
      console.error(e);
    } finally {
      state.isRendering = false;
    }
  }

  // Listen for product added events to set last-added context
  document.addEventListener('product-ajax:added', (evt) => {
    // Try to derive included handles from the clicked button if available
    let handles = null;
    try {
      const trigger = evt.detail?.product?.trigger || document.activeElement;
      const attr = trigger && trigger.getAttribute && trigger.getAttribute('data-included-training-handles');
      if (attr) {
        handles = attr.split(',').map((s) => s.trim()).filter(Boolean);
      }
    } catch (e) {}

    state.lastAddedHandles = handles && handles.length ? handles : getPdpHandles();
    // render on next drawer open; if drawer is already open, render now
    render();
  });

  // Render when drawer content is refreshed
  document.addEventListener('cart:updated', () => {
    // After cart updates, re-evaluate to hide already-added suggestions
    render();
  });

  // When the cart drawer opens, render using last-added or PDP fallback
  document.addEventListener('cart:open', () => {
    render();
  });

  // Initial render on PDP when drawer is present
  if (document.querySelector('cart-drawer')) {
    render();
  }
})();


